<script setup lang="ts">
/**
 * Bandeau d'icones globales du joueur (Phase 2 de la refonte nav).
 *
 * Remplace la sidebar gauche pour les destinations meta inter-planetes :
 * messages, quetes, recherche globale, inventaire, chat + un dropdown
 * "Plus" pour les vues secondaires (lobby, actualites, classement, stats,
 * admin).
 *
 * Place dans AppHeader, juste a gauche du dropdown utilisateur. Cache
 * sur mobile (la MobileBottomNav prend le relais).
 *
 * Visibilite : la majorite des entrees sont in-universe. "Lobby" est
 * affichee uniquement hors univers. "Admin" est toujours visible
 * (le filtrage fin de permission est cote backend).
 */
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useSessionStore } from '@/stores/session.store';
import { useRewardChestsStore } from '@/stores/rewardChests.store';
import { useResearchStore } from '@/stores/research.store';
import { useAuthStore } from '@/stores/auth.store';
import { useInboxStore } from '@/stores/inbox.store';
import { useQuestsStore } from '@/stores/quests.store';
import { useChatUnreadStore } from '@/stores/chat.store';
import { useOverlaysStore } from '@/stores/overlays.store';
import { useInventoryNewStore } from '@/stores/inventoryNew.store';
import HeaderNotificationsBell from './HeaderNotificationsBell.vue';

const route = useRoute();
const router = useRouter();
const overlays = useOverlaysStore();

/** Les destinations méta s'ouvrent en MODALE (overlay) plutôt qu'en page. */
type OverlayKind = 'inbox' | 'inventory' | 'rewards' | 'quests' | 'research' | 'chat';
function openOverlay(which: OverlayKind): void {
  switch (which) {
    case 'inbox': overlays.openInbox(); break;
    case 'rewards': overlays.openRewardChests(); break;
    case 'inventory': overlays.openInventory(); break;
    case 'quests': overlays.openQuests(); quests.acknowledgeAvailable(); break;
    case 'research': overlays.openResearchGlobal(); research.acknowledgeResearch(); break;
    case 'chat': overlays.openChat(); break;
  }
}

// Navigation des items du dropdown "Plus". On passe par @command + router.push
// plutôt que d'envelopper chaque el-dropdown-item dans un <router-link> :
// Element Plus intercepte le clic de l'item, ce qui empêchait la navigation
// (le menu se fermait sans rien faire -> "Admin" et les autres inaccessibles).
function onExtraCommand(to: string): void {
  if (to && to !== route.path) router.push(to);
}
const session = useSessionStore();
const research = useResearchStore();
const auth = useAuthStore();
const inbox = useInboxStore();
const quests = useQuestsStore();
const chatUnread = useChatUnreadStore();
const rewardChests = useRewardChestsStore();
const inventoryNew = useInventoryNewStore();

// Coffres de progression : badge = nombre de coffres non ouverts. Rafraîchi à
// l'entrée en univers (le header est monté in-universe). Idem inventaire pour le
// compteur d'objets « nouveaux ».
onMounted(() => { if (session.inUniverse) { void rewardChests.refresh(); void inventoryNew.refresh(); void research.refreshGlobalCount(); } });
watch(() => session.inUniverse, (v) => { if (v) { void rewardChests.refresh(); void inventoryNew.refresh(); void research.refreshGlobalCount(); } });

// Routes dont le badge est « passif » (couleur info, pas rouge) : compteurs
// d'information (nouveaux objets) plutôt qu'alertes à traiter.
const PASSIVE_BADGE_ROUTES = new Set(['/inventory']);

// Petit POINT passif (sans nombre) : attire l'œil sur une nouveauté quand il
// n'y a pas déjà un compteur. Quêtes : nouvelle quête disponible.
function dotFor(route: string): boolean {
  if (route === '/quests') return quests.hasNewQuests;
  if (route === '/research/global') return research.hasNewResearch;
  return false;
}

// Formatte un compteur badge : 0 -> null (cache), >99 -> "99+", sinon nombre.
function formatBadge(n: number): string | null {
  if (n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

// Map route -> badge a afficher dessus. Une route absente = pas de badge.
const badges = computed<Record<string, string | null>>(() => ({
  '/inbox':  formatBadge(inbox.unreadCount),
  '/chat':   formatBadge(chatUnread.unreadCount),
  '/quests': formatBadge(quests.claimableCount),
  '/rewards': formatBadge(rewardChests.count),
  '/inventory': formatBadge(inventoryNew.newCount),
}));

interface NavIcon {
  to: string;
  label: string;
  icon: string;
  /** Ouvre une modale overlay au lieu de naviguer (méta inter-planètes). */
  overlay?: OverlayKind;
  /** Affichee uniquement quand le joueur n'a pas d'univers actif. */
  hideInUniverse?: boolean;
  /** Affichee uniquement quand le joueur est dans un univers. */
  hideOutOfUniverse?: boolean;
  /** Code de recherche requise (>= niveau 1). */
  requiresResearch?: string;
  /** Entrée grisée / non cliquable (ex. Alliance : accès désormais via le
   *  module planète débloqué par une Ambassade). */
  disabled?: boolean;
  /** Infobulle affichée sur une entrée désactivée. */
  disabledHint?: string;
}

const GALAXY_GATE_RESEARCH = 'r31_hyperdrive';

// === Icones principales (visibles en permanence) ===
const mainItems: NavIcon[] = [
  // Accueil = homepage du joueur (planete mere + globe 3D). Remplace l'ancien
  // bouton "Mes planetes" : un seul point d'entree maison, visible en 1 clic.
  { to: '/',               label: 'Accueil',     icon: 'House' },
  { to: '/inbox',          label: 'Messages',    icon: 'Message',      hideOutOfUniverse: true, overlay: 'inbox' },
  { to: '/quests',         label: 'Quetes',      icon: 'Flag',         hideOutOfUniverse: true, overlay: 'quests' },
  { to: '/research/global', label: 'Recherches', icon: 'Cpu',          hideOutOfUniverse: true, overlay: 'research' },
  { to: '/inventory',      label: 'Inventaire',  icon: 'Box',          hideOutOfUniverse: true, overlay: 'inventory' },
  { to: '/rewards',        label: 'Récompenses', icon: 'Present',      hideOutOfUniverse: true, overlay: 'rewards' },
  { to: '/chat',           label: 'Chat',        icon: 'ChatDotRound', hideOutOfUniverse: true, overlay: 'chat' },
  {
    to: '/cosmos',
    label: 'Cosmos',
    icon: 'Discover',
    hideOutOfUniverse: true,
    requiresResearch: GALAXY_GATE_RESEARCH,
  },
];

// === Dropdown "Plus" (vues secondaires) ===
const extraItems: NavIcon[] = [
  { to: '/lobby',       label: 'Lobby univers', icon: 'Compass',     hideInUniverse: true },
  { to: '/alliance',    label: 'Alliance',      icon: 'OfficeBuilding', hideOutOfUniverse: true,
    disabled: true, disabledHint: 'Accessible via le module Alliance d\'une planète (nécessite une Ambassade).' },
  { to: '/achievements', label: 'Accomplissements', icon: 'Medal',   hideOutOfUniverse: true },
  { to: '/news',        label: 'Actualites',    icon: 'Bell',        hideOutOfUniverse: true },
  { to: '/leaderboard', label: 'Classement',    icon: 'Trophy',      hideOutOfUniverse: true },
  { to: '/me/stats',    label: 'Mes stats',     icon: 'TrendCharts', hideOutOfUniverse: true },
  // Communauté & référence — publiques, accessibles partout (y compris hors univers).
  { to: '/forum',       label: 'Forum',         icon: 'ChatLineSquare' },
  { to: '/library',     label: 'Bibliothèque',  icon: 'Reading' },
  { to: '/admin',       label: 'Admin',         icon: 'Setting' },
];

function isVisible(item: NavIcon): boolean {
  if (item.to === '/admin' && !auth.isAdmin) return false;
  if (item.hideInUniverse && session.inUniverse) return false;
  if (item.hideOutOfUniverse && !session.inUniverse) return false;
  if (item.requiresResearch && !research.hasResearch(item.requiresResearch)) return false;
  return true;
}

const visibleMain = computed(() => mainItems.filter(isVisible));
const visibleExtra = computed(() => extraItems.filter(isVisible));

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/';
  return route.path === to || route.path.startsWith(to + '/');
}
</script>

<template>
  <nav class="header-global-nav" aria-label="Navigation globale">
    <template v-for="item in visibleMain" :key="item.to">
      <!-- Inbox / Inventaire : ouvrent une modale (overlay) au lieu de naviguer. -->
      <button
        v-if="item.overlay"
        type="button"
        class="hgn-icon"
        :title="badges[item.to] ? `${item.label} (${badges[item.to]})` : item.label"
        :aria-label="item.label"
        @click="openOverlay(item.overlay)"
      >
        <el-icon :size="20"><component :is="item.icon" /></el-icon>
        <span
          v-if="badges[item.to]"
          class="hgn-badge"
          :class="{ 'hgn-badge--passive': PASSIVE_BADGE_ROUTES.has(item.to) }"
          aria-hidden="true"
        >{{ badges[item.to] }}</span>
        <span v-else-if="dotFor(item.to)" class="hgn-dot" aria-hidden="true" />
      </button>
      <router-link
        v-else
        :to="item.to"
        class="hgn-icon"
        :class="{ 'is-active': isActive(item.to) }"
        :title="badges[item.to] ? `${item.label} (${badges[item.to]})` : item.label"
        :aria-label="item.label"
      >
        <el-icon :size="20"><component :is="item.icon" /></el-icon>
        <span v-if="badges[item.to]" class="hgn-badge" aria-hidden="true">{{ badges[item.to] }}</span>
      </router-link>
    </template>

    <!-- Cloche de notifications (aperçu rapide des derniers messages inbox). -->
    <HeaderNotificationsBell v-if="session.inUniverse" />

    <!-- "Plus" dropdown : tout ce qui n'a pas sa place dans le bandeau principal. -->
    <el-dropdown
      v-if="visibleExtra.length > 0"
      trigger="click"
      placement="bottom-end"
      @command="onExtraCommand"
    >
      <!-- Pas d'AppTooltip ici : envelopper le trigger d'el-dropdown dans un
           tooltip casse l'ouverture du menu (le clic est consomme par le
           tooltip). On garde un title natif pour l'infobulle "Plus". -->
      <span class="hgn-icon hgn-more" aria-label="Plus" title="Plus">
        <el-icon :size="20"><MoreFilled /></el-icon>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="item in visibleExtra"
            :key="item.to"
            :command="item.to"
            :disabled="item.disabled"
            :title="item.disabled ? item.disabledHint : undefined"
            :class="{ 'is-active': isActive(item.to) }"
          >
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.label }}</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </nav>
</template>

<style lang="scss" scoped>
.header-global-nav {
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 767.99px) {
    display: none;
  }
}

.hgn-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: $radius-md;
  color: $text-secondary;
  text-decoration: none;
  cursor: pointer;
  // Reset pour les déclencheurs <button> (Inbox/Inventaire en modale).
  border: none;
  background: transparent;
  font: inherit;
  padding: 0;
  transition: background $duration-fast $easing-default,
              color $duration-fast $easing-default;

  &:hover {
    background: $bg-tertiary;
    color: $color-neon-cyan;
  }

  &.is-active {
    background: rgba($accent-primary, 0.18);
    color: $color-neon-cyan;
  }
}

.hgn-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: $color-danger;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-family: $font-mono;
  box-shadow: 0 0 0 2px $bg-primary;
  pointer-events: none;

  // Variante « passive » : compteur d'information (objets nouveaux) — couleur
  // info (cyan) au lieu du rouge d'alerte.
  &--passive {
    background: $color-neon-cyan;
    color: #04222b;
  }
}

// Point passif (nouveauté sans compteur, ex. nouvelle quête dispo).
.hgn-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: $color-neon-cyan;
  box-shadow: 0 0 6px rgba($color-neon-cyan, 0.9), 0 0 0 2px $bg-primary;
  pointer-events: none;
}

.hgn-extra-link {
  text-decoration: none;
  color: inherit;
  display: block;

  .is-active {
    color: $color-neon-cyan;
    font-weight: $font-weight-bold;
  }
}
</style>
